/**
 * IPD reports API (day-1 S12 "Today's IPD report", ADDENDUM-3/F-BE frozen
 * contract; RBAC-IPD FE-1). Both routes are PERMISSION-gated server-side
 * (`require_permission(VIEW_IPD_REVENUE)`) — mirrors the FE gate in
 * `constants/admission.js`'s `REPORT_PERMISSIONS.VIEW_IPD_REVENUE`.
 */

import apiClient from './apiClient';

const BASE = '/api/organisation/reports/ipd-deposits';

function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, message, ...extras } = detail;
    return { error: message || fallback, code, ...extras };
  }
  if (typeof detail === 'string') return { error: detail };
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission to view this report.' };
    case 422: return { error: 'That date is invalid.', code: 'INVALID_DATE' };
    case 503: return { error: 'Reports are temporarily unavailable. Try again shortly.', code: 'IPD_TABLE_UNAVAILABLE' };
    default:  return { error: fallback };
  }
}

/**
 * GET /reports/ipd-deposits?date= — today's (or the given date's) admitted-
 * patient deposits. `date` omitted ⇒ server defaults to org-local today.
 * Envelope: { date, rows: DepositRow[], count, day_total_paise,
 * refunds: RefundRow[], refunds_total_paise }. Response passed through
 * verbatim (no field whitelist) — additive fields flow to the dialog.
 */
export const getIpdDepositsReport = async (date) => {
  try {
    const response = await apiClient.get(BASE, { params: date ? { date } : {} });
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[IpdReportsAPI] getIpdDepositsReport error:', error);
    return { success: false, ...parseError(error, 'Failed to load the deposits report.') };
  }
};

/**
 * GET /reports/ipd-deposits/pdf?date= — same blob + in-app FileViewerStore
 * pattern as the interim-invoice print (never a raw window.open).
 */
export const openIpdDepositsReportPdf = async (date) => {
  try {
    const response = await apiClient.get(`${BASE}/pdf`, {
      params: date ? { date } : {},
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({ blobUrl, fileType: 'pdf', title: "Today's IPD report" });
    return { success: true, blobUrl };
  } catch (error) {
    console.error('[IpdReportsAPI] openIpdDepositsReportPdf error:', error);
    return { success: false, ...parseError(error, 'Could not open the report.') };
  }
};

const ipdReportsApi = { getIpdDepositsReport, openIpdDepositsReportPdf };

export default ipdReportsApi;
